import React, { useMemo } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { useManagementCompanyLogoBlobUrl } from '../../wrh/reports/useManagementCompanyLogo';
import {
  buildBoardLines,
  filterBoardLines,
  isoToday,
} from '../replacementPlannerUtils';
import type { CrewReplacementResponse, CrewVesselListResponse } from '../types';
import { ReplacementPlanReport } from './ReplacementPlanReport';

function buildBackPath(params: URLSearchParams): string {
  const vesselId = params.get('vesselId')?.trim() ?? '';
  if (!vesselId) return '/crew/replacement-planner';
  return `/crew/replacement-planner?vesselId=${encodeURIComponent(vesselId)}`;
}

const ReplacementPlanReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();

  const vesselId = searchParams.get('vesselId')?.trim() ?? '';
  const withinDaysRaw = searchParams.get('withinDays')?.trim() || '90';
  const withinDays = Number(withinDaysRaw) || 90;
  const needsReliefOnly = searchParams.get('needsReliefOnly') !== '0';
  const today = isoToday();

  const vesselListUrl = vesselId
    ? `/crew/vessel-list?vesselId=${encodeURIComponent(vesselId)}&periodFrom=${today}&periodTo=${today}`
    : null;
  const replUrl = vesselId
    ? `/crew/replacements?vesselId=${encodeURIComponent(vesselId)}&includeCancelled=0`
    : null;

  const { data: vesselList, error: vesselError, isLoading: vesselLoading } =
    useSWR<CrewVesselListResponse>(vesselListUrl, fetcher, { revalidateOnFocus: false });
  const { data: replData, error: replError, isLoading: replLoading } =
    useSWR<CrewReplacementResponse>(replUrl, fetcher, { revalidateOnFocus: false });

  const lines = useMemo(() => {
    const onboard = (vesselList?.rows ?? []).filter(
      (r) => r.status === 'ONB' || (!r.signOffDate && !r.status),
    );
    const all = buildBoardLines(onboard, replData?.rows ?? []);
    return filterBoardLines(all, { needsReliefOnly, withinDays });
  }, [vesselList?.rows, replData?.rows, needsReliefOnly, withinDays]);

  const managementCompany = useMemo(() => {
    const manager = vesselList?.vessel?.manager;
    if (manager?.id) {
      return {
        id: manager.id,
        name: manager.name,
        logoStorageKey: manager.logoStorageKey ?? null,
      };
    }
    return null;
  }, [vesselList?.vessel?.manager]);

  const { logoBlobUrl: companyLogoSrc } = useManagementCompanyLogoBlobUrl(
    managementCompany?.id,
    managementCompany?.logoStorageKey,
  );

  const backTo = buildBackPath(searchParams);
  const isLoading = vesselLoading || replLoading;

  if (!vesselId) {
    return <Navigate to="/crew/replacement-planner" replace />;
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading Replacement plan…
      </div>
    );
  }

  if (vesselError || replError || !vesselList?.vessel) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load replacement plan.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to Replacement planner
        </Link>
      </div>
    );
  }

  const subtitle = `${vesselList.vessel.vesselName} · ${
    needsReliefOnly ? `needs relief · ${withinDays}d` : 'all onboard'
  } · ${lines.length} line${lines.length === 1 ? '' : 's'}`;

  return (
    <ReportView
      backTo={backTo}
      backLabel="Back to Replacement planner"
      title="Replacement plan"
      subtitle={subtitle}
      printPageSize="A4 landscape"
      printMargin="0"
    >
      <ReplacementPlanReport
        data={vesselList}
        lines={lines}
        withinDays={withinDays}
        needsReliefOnly={needsReliefOnly}
        companyLogoSrc={companyLogoSrc}
        companyName={managementCompany?.name}
      />
    </ReportView>
  );
};

export default ReplacementPlanReportPage;
